import { useEffect, useState } from "react";
import { ShieldWarning, DownloadSimple } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { api } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/rejected-leads", label: "Rejected Leads", icon: <ShieldWarning size={18} weight="duotone" />, testId: "nav-admin-rejected-leads" }];

const LAYER_LABELS = {
  phone_verification: "Layer 1 — Phone Verification",
  dnc_scrub: "Layer 2 — DNC Scrub",
  email_verification: "Layer 3 — Email Verification",
};

export default function AdminRejectedLeadsPage() {
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchRejected = async () => {
      try {
        const { data } = await api.get("/admin/rejected-leads", { params: { limit: 500 } });
        setRejected(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchRejected();
  }, []);

  return (
    <DashboardLayout navItems={navItems} moduleName="Rejected Leads" moduleBackPath="/admin">
      <div data-testid="admin-rejected-leads-page">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Read-only log of leads that never reached a rep because they failed phone, Do-Not-Call, or email verification.">Rejected Leads</h1>
            <p className="text-igc-muted text-sm">Inbound leads dropped by the 3-layer verification pipeline (phone, DNC, email) before ever reaching a rep. Read-only audit log.</p>
            <p className="text-igc-muted text-xs mt-1" data-testid="rejected-leads-scope-note">This table shows only the most recent 500 (newest first) — <span className="text-igc-goldText">Export CSV</span> downloads the complete history since Sep 10, 2026.</p>
          </div>
          <a
            data-testid="rejected-leads-export-csv-button"
            href={`${api.defaults.baseURL}/admin/rejected-leads/export`}
            target="_blank"
            rel="noreferrer"
            title="Downloads a CSV of every lead that failed DNC, phone (NumVerify), or email (MillionVerifier) verification since Sep 10, 2026 — for the DataMoon refund case."
            className="shrink-0 flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide font-medium bg-igc-gold text-black hover:bg-igc-goldHover transition-all hover:-translate-y-0.5"
          >
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </a>
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="rejected-leads-loading">Loading...</p>}

        {!loading && error && (
          <p className="text-igc-danger text-sm" data-testid="rejected-leads-error-state">Couldn't load rejected leads. Please refresh the page or try again shortly.</p>
        )}

        {!loading && !error && rejected.length === 0 && (
          <p className="text-igc-muted text-sm" data-testid="rejected-leads-empty-state">No leads have been rejected by the verification pipeline yet.</p>
        )}

        {!loading && !error && rejected.length > 0 && (
          <div className="bg-igc-surface border border-igc-gold/15 overflow-x-auto">
            <table className="w-full text-sm" data-testid="rejected-leads-table">
              <thead>
                <tr className="border-b border-igc-gold/15 text-igc-muted text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Layer Failed</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Rejected On</th>
                </tr>
              </thead>
              <tbody>
                {rejected.map((r) => (
                  <tr key={r.id} className="border-b border-igc-gold/10 text-igc-cream" data-testid={`rejected-lead-row-${r.id}`}>
                    <td className="px-4 py-3">{r.name || "—"}</td>
                    <td className="px-4 py-3 text-igc-muted">{r.phone || "—"}</td>
                    <td className="px-4 py-3 text-igc-muted">{r.email || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-danger/15 text-igc-danger uppercase border border-igc-danger/30 whitespace-nowrap" data-testid={`rejected-lead-layer-${r.id}`}>
                        {LAYER_LABELS[r.layer_failed] || r.layer_failed}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-igc-muted">{r.reason}</td>
                    <td className="px-4 py-3 text-igc-muted">{r.source}</td>
                    <td className="px-4 py-3 text-igc-muted whitespace-nowrap">{r.timestamp ? formatET(r.timestamp, "MMM d, yyyy h:mm a") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
